import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { classifyBookingResults } from '../../../utils/appointmentWorkflow';
import { useSearchParams, useParams } from 'react-router-dom';
import {
  CardMedia,
  Typography,
  Box,
  Skeleton,
  Chip,
  Container,
  Paper,
  Fade,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AccessTime,
  ArrowBack,
  ArrowBackIos,
  ArrowForwardIos,
  CurrencyExchange,
  Phone,
  LocationOn,
  Search,
  Info
} from '@mui/icons-material';
import HomeIcon from '@mui/icons-material/Home';
import { useServicesPublicSalonListQuery, useSalonsRetrieveQuery, useAppointmentsRetrieveQuery, useTeamsTeamMembersSalonMembersRetrieveQuery } from '../../../services/GLOWClient';
import { getLocalizedField } from '../../../utils/localizedFields';
import { useNavigate } from 'react-router-dom';
import { useSalonPublicTheme } from '../../../context/SalonPublicThemeContext';
import { BASE_URL } from '../../../config';
import AppointmentCalendar from './AppointmentCalendar';
import ManualBookingNotice from './ManualBookingNotice';
import AppointmentTypeSelector from './AppointmentTypeSelector';
import ServiceSelectionStep from './ServiceSelectionStep';
import ProfessionalSelectionStep from './ProfessionalSelectionStep';
import DateTimeSelectionStep from './DateTimeSelectionStep';
import ConsecutiveServiceBooking from './ConsecutiveServiceBooking';
import AppointmentConfirmationStep from './AppointmentConfirmationStep';
import AuthModal from '../../Auth/AuthModal';
import { useAuth } from '../../../context/AuthContext';
import { useAppointmentsCreateMutation } from '../../../services/GLOWClient';
import dayjs from 'dayjs';
import { HIGH_DENSITY_TABLET_MEDIA } from '../../../utils/responsiveMedia';
import Button from '../../common/salonPublicAppearance/SalonPublicButton';
import IconButton from '../../common/salonPublicAppearance/SalonPublicIconButton';

// Helper function to build image URL
const buildImageUrl = (url) => {
  if (!url) return '';

  if (/^https?:\/\//i.test(url)) {
    if (url.match(/^http:\/\/localhost\//) && !url.match(/^http:\/\/localhost:[0-9]+\//)) {
      return url.replace('http://localhost/', 'http://localhost:8012/');
    }
    return url;
  }

  if (url.startsWith('/media/')) {
    const baseUrl = window.location.hostname === 'localhost' ?
      'http://localhost:8012' :
      BASE_URL.replace(/\/$/, '');
    return `${baseUrl}${url}`;
  }

  if (url.startsWith('media/')) {
    const baseUrl = window.location.hostname === 'localhost' ?
      'http://localhost:8012' :
      BASE_URL.replace(/\/$/, '');
    return `${baseUrl}/${url}`;
  }

  const baseUrl = window.location.hostname === 'localhost' ?
    'http://localhost:8012' :
    BASE_URL.replace(/\/$/, '');
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${baseUrl}${cleanUrl}`;
};

const ClientServices = ({ salonId, existingServices = [], startWithTypeSelection = false, onResetStartBooking }) => {
  const { t, i18n } = useTranslation();
  const { tokens: theme } = useSalonPublicTheme();

  const getTranslatedField = (item, field) => {
    return getLocalizedField(item, field, i18n.language);
  };

  const [showAppointmentBooking, setShowAppointmentBooking] = useState(false);
  const [searchParams] = useSearchParams();
  const { salonId: urlSalonId } = useParams();
  const navigate = useNavigate();

  // Check URL params
  const serviceParam = searchParams.get('service');
  const startTimeParam = searchParams.get('start_time');
  const rebookAppointmentId = searchParams.get('rebookAppointment');
  const startBookingParam = searchParams.get('startBooking') === 'true';

  // bookingStep: 'services' | 'type_selection' | 'service_selection' | 'calendar' | 'consecutive'
  const [bookingStep, setBookingStep] = useState(
    (startWithTypeSelection || startBookingParam) ? 'type_selection' : 'services'
  );
  // bookingType: 'simple' | 'consecutive'
  const [bookingType, setBookingType] = useState('simple');
  const [selectedProMap, setSelectedProMap] = useState({}); // { serviceId: employeeId }

  const [selectedService, setSelectedService] = useState(null);
  const [selectedServices, setSelectedServices] = useState(existingServices);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Toutes');
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);

  // Booking Confirmation State
  const [bookingSlot, setBookingSlot] = useState(null); // { date, slot }
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [bookingError, setBookingError] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [bookingOutcome, setBookingOutcome] = useState('confirmed');
  const [isPendingBooking, setIsPendingBooking] = useState(false);

  const { user, isAuthenticated } = useAuth();
  const [createAppointment, { isLoading: isCreatingAppointment }] = useAppointmentsCreateMutation();
  const [selectedServiceForDescription, setSelectedServiceForDescription] = useState(null);

  const handleConfirmBooking = async () => {
    setBookingError(null);

    // 1. Check Authentication
    if (!isAuthenticated()) {
      setIsPendingBooking(true);
      setAuthModalOpen(true);
      return;
    }

    if (!bookingSlot || !selectedServices.length) {
      setBookingError(t("client_services.missing_booking_info"));
      return;
    }

    // 2. Prepare Appointments Payload
    try {
      const { date, slot } = bookingSlot; // slot can be 'HH:mm' or Chain object

      const appointmentPromises = selectedServices.map(async (service, index) => {
        let startTimeStr = '';
        let teamMemberId = null;

        if ((typeof slot === 'object' && slot.services)) {
          // Multi/Consecutive mode
          const chainService = slot.services[index];
          if (!chainService) throw new Error(t("client_services.sync_error"));

          startTimeStr = chainService.service_start;
          teamMemberId = chainService.team_member_id; // Resolved ID from chain
        } else {
          // Single mode
          startTimeStr = typeof slot === 'object' ? slot.start_time : slot; // HH:mm

          if (service.teamMember && service.teamMember !== 'any') {
            teamMemberId = Number(service.teamMember);
          } else {
            // If 'any', pass null to backend (it should handle finding an available pro or assigning one)
            // OR passing null might fail if backend expects strict ID. 
            // Based on AppointmentCalendar, if 'any', we should have resolved it. 
            // For now, sending null.
            teamMemberId = null;
          }
        }

        // Format DateTimes
        const startDateTime = dayjs(`${date}T${startTimeStr}:00`);
        const duration = service.duration || 60;
        const endDateTime = startDateTime.add(duration, 'minute');

        // Fix for salonId: use effectiveSalonId or urlSalonId
        const targetSalonId = Number(salonId || urlSalonId);
        // Fix for client ID: user.id
        const clientId = Number(user?.id);

        const payload = {
          salon: targetSalonId,
          service: Number(service.service_id || service.serviceId || service.id),
          team_member: teamMemberId,
          client: clientId,
          start_time: startDateTime.format('YYYY-MM-DD HH:mm:ss'),
          end_time: endDateTime.format('YYYY-MM-DD HH:mm:ss'),
        };

        const result = await createAppointment({ appointmentCreateRequest: payload }).unwrap();
        return result;
      });

      const results = await Promise.all(appointmentPromises);
      setBookingOutcome(classifyBookingResults(results));

      // Success
      setShowSuccessDialog(true);

    } catch (err) {
      console.error(t("client_services.booking_error"), err);
      const detail = err?.data?.detail || err?.data?.error || t("client_services.unknown_error");
      setBookingError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      alert(`${t('common.error')}: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
    }
  };

  // Retry booking after successful login
  useEffect(() => {
    if (user && isPendingBooking) {
      // User just logged in and we have a pending booking
      setAuthModalOpen(false);
      setIsPendingBooking(false);
      // Wait a tick to ensure state is settled? Not strictly necessary with useEffect dependency on user
      handleConfirmBooking();
    }
  }, [user, isPendingBooking, user?.id]); // Added user?.id to dependency array for robustness

  const effectiveSalonId = salonId || urlSalonId;

  const { data: salonDetails } = useSalonsRetrieveQuery(
    { salonId: effectiveSalonId, language: i18n.language },
    { skip: !effectiveSalonId }
  );

  const { data: allServices, error: servicesError, isLoading: servicesLoading } = useServicesPublicSalonListQuery(
    { salonId: effectiveSalonId },
    { skip: !effectiveSalonId }
  );

  // Fetch salon members to know which services have at least one employee assigned
  const {
    data: salonMembersData,
    isLoading: salonMembersLoading,
  } = useTeamsTeamMembersSalonMembersRetrieveQuery(
    effectiveSalonId ? { salon: effectiveSalonId } : {},
    { skip: !effectiveSalonId }
  );

  const salonMembers = useMemo(() => {
    if (!salonMembersData) return [];
    if (Array.isArray(salonMembersData)) return salonMembersData;
    return salonMembersData.results || [];
  }, [salonMembersData]);

  // Build a set of service IDs that have at least one employee who can perform them
  const serviceIdsWithEmployees = useMemo(() => {
    const set = new Set();
    if (!salonMembers || salonMembers.length === 0) return set;

    salonMembers.forEach((member) => {
      const serviceIds = Array.isArray(member.service_ids) ? member.service_ids : [];
      serviceIds.forEach((id) => {
        if (id === null || id === undefined) return;
        set.add(Number(id));
      });
    });

    return set;
  }, [salonMembers]);

  // Fetch appointment data for rebooking
  const { data: rebookAppointmentData } = useAppointmentsRetrieveQuery(
    { appointmentId: rebookAppointmentId },
    { skip: !rebookAppointmentId }
  );

  const categories = useMemo(() => {
    if (!allServices) return [];
    const uniqueCategories = {};
    allServices.forEach(service => {
      if (service.category_details) {
        const catId = service.category_details.category_id || service.category_details.id;
        if (catId && !uniqueCategories[catId]) {
          uniqueCategories[catId] = {
            category_id: catId,
            name: service.category_details.name,
            salon: service.category_details.salon
          };
        }
      }
    });
    return Object.values(uniqueCategories);
  }, [allServices]);

  useEffect(() => {
    if (!allServices || !categories || allServices.length === 0) {
      setServicesByCategory({});
      return;
    }

    const organized = {};

    categories.forEach(category => {
      organized[category.category_id] = {
        categoryName: category.name,
        services: []
      };
    });

    organized['uncategorized'] = {
      categoryName: t("client_services.others"),
      services: []
    };

    allServices.forEach(service => {
      const categoryId = service.category_details?.category_id || service.category_details?.id;

      if (categoryId && organized[categoryId]) {
        organized[categoryId].services.push(service);
      } else {
        organized['uncategorized'].services.push(service);
      }
    });

    Object.keys(organized).forEach(catId => {
      if (catId !== 'uncategorized' && organized[catId].services.length === 0) {
        delete organized[catId];
      }
    });

    setServicesByCategory(organized);
  }, [allServices, categories]);

  const [servicesByCategory, setServicesByCategory] = useState({});
  const [currentImageIndices, setCurrentImageIndices] = useState({});

  const nextImage = (serviceId) => {
    setCurrentImageIndices(prev => {
      const currentIndex = prev[serviceId] || 0;
      const service = Object.values(servicesByCategory)
        .flatMap(cat => cat.services)
        .find(s => s.service_id === serviceId);

      if (!service || !service.images || service.images.length <= 1) return prev;

      return {
        ...prev,
        [serviceId]: (currentIndex + 1) % service.images.length
      };
    });
  };

  const prevImage = (serviceId) => {
    setCurrentImageIndices(prev => {
      const currentIndex = prev[serviceId] || 0;
      const service = Object.values(servicesByCategory)
        .flatMap(cat => cat.services)
        .find(s => s.service_id === serviceId);

      if (!service || !service.images || service.images.length <= 1) return prev;

      return {
        ...prev,
        [serviceId]: (currentIndex - 1 + service.images.length) % service.images.length
      };
    });
  };

  useEffect(() => {
    if (serviceParam && allServices && allServices.length > 0) {
      const serviceId = parseInt(serviceParam, 10);
      const serviceToSelect = allServices.find(service => service.service_id === serviceId);

      if (serviceToSelect) {
        setSelectedService(serviceToSelect);
        setShowAppointmentBooking(true);
      }
    }
  }, [serviceParam, allServices]);

  // Handle rebooking scenario - don't auto-redirect, let user choose service
  useEffect(() => {
    if (rebookAppointmentData && allServices && allServices.length > 0) {
      // Find the service from the appointment data for reference, but don't auto-select
      const serviceToSelect = allServices.find(service =>
        service.service_id === rebookAppointmentData.service
      );

      // Only set the selected service for reference, don't show booking calendar automatically
      if (serviceToSelect) {
        setSelectedService(serviceToSelect);
        // Don't auto-show booking: setShowAppointmentBooking(true);
      }
    }
  }, [rebookAppointmentData, allServices]);

  // Render skeleton for services section only
  const renderServicesSkeleton = () => (
    <>
      {/* Nos Prestations Section Skeleton */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, background: theme.salon.surfaceMuted, border: `1px solid ${theme.colors.border.light}` }}>
        <Skeleton variant="text" width="40%" height={40} sx={{ mx: 'auto', mb: 3 }} />

        {/* Category Chips Skeleton */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mb: 3 }}>
          <Skeleton variant="rectangular" width={120} height={32} sx={{ borderRadius: 2 }} />
        </Box>

        {/* Search Bar Skeleton */}
        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 2, mb: 3 }} />

        {/* Category Filter Chips Skeleton */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rectangular" width={100} height={32} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      </Paper>

      {/* Services List Skeleton */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, px: { xs: 0, md: 6, lg: 8 } }}>
        {[1, 2].map((categoryIndex) => (
          <Box key={categoryIndex} sx={{ mb: 5 }}>
            {/* Category Title Skeleton */}
            <Skeleton variant="text" width="30%" height={40} sx={{ mb: 3 }} />

            {/* Services White Box Skeleton */}
            <Box sx={{ backgroundColor: 'white', borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)' }}>
              {[1, 2, 3].map((serviceIndex) => (
                <Box
                  key={serviceIndex}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 3,
                    p: 3,
                    borderBottom: serviceIndex < 3 ? `1px solid ${theme.colors.border.light}` : 'none',
                  }}
                >
                  {/* Service Name & Description Skeleton */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Skeleton variant="text" width="60%" height={28} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="90%" height={20} />
                    <Skeleton variant="text" width="70%" height={20} />
                  </Box>

                  {/* Duration & Price Skeleton */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 120 }}>
                    <Skeleton variant="text" width={80} height={24} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width={100} height={32} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </>
  );

  // Show loading skeleton while services are loading
  if (servicesLoading) {
    return renderServicesSkeleton();
  }

  if (servicesError) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: 'error.lighter',
            borderRadius: 3
          }}
        >
          <Typography
            variant="h5"
            color="error.main"
            gutterBottom
          >
            {t("client_services.error_loading_services")}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
          >
            {servicesError.message || t("client_services.unknown_error")}
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ mt: 3 }}
          >
            {t("client_services.retry")}
          </Button>
        </Paper>
      </Container>
    );
  }

  if (!allServices || allServices.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: theme.colors.primary[50],
            borderRadius: 3
          }}
        >
          <Typography
            variant="h5"
            color="text.primary"
            gutterBottom
          >
            {t("client_services.no_services_available")}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
          >
            {t("client_services.salon_has_no_services")}
          </Typography>
        </Paper>
      </Container>
    );
  }

  const handleAppointmentBookingClose = () => {
    setShowAppointmentBooking(false);
  };

  const handleCancelBookingFlow = () => {
    setBookingStep('services');
    setSelectedServices([]);
    setBookingSlot(null);
    setShowAppointmentBooking(false);
    navigate(`/salon/${effectiveSalonId}`);
  };

  const handleAppointmentSuccess = (services) => {
    setSelectedServices(services);
    setShowAppointmentBooking(false);
  };

  const renderUnifiedHeader = () => {
    const isStepView = bookingStep !== 'services';
    if (!isStepView) return null;

    const stepTitles = {
      'type_selection': t("client_services.appointment_type"),
      'service_selection': t("client_services.service_selection"),
      'pro_selection': t("client_services.professional_selection"),
      'calendar': t("client_services.date_time"),
      'consecutive': t("client_services.consecutive_booking"),
      'confirmation': t("client_services.confirmation")
    };

    const salonPhoto = salonDetails?.photos && salonDetails.photos.length > 0
      ? buildImageUrl(typeof salonDetails.photos[0] === 'string' ? salonDetails.photos[0] : salonDetails.photos[0]?.image || salonDetails.photos[0]?.url || '')
      : 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1200&h=400&fit=crop';

    return (
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '100px', sm: '120px', md: '140px' },
          borderRadius: 4,
          overflow: 'hidden',
          mb: 5,
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {/* Background Layer */}
        <Box
          sx={{
            position: 'absolute',
            top: 0, left: 0, width: '100%', height: '100%',
            backgroundImage: `url(${salonPhoto})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.5)',
          }}
        />

        {/* Overlay for better readability - Gradient beige/orange */}
        <Box
          sx={{
            position: 'absolute',
            top: 0, left: 0, width: '100%', height: '100%',
            background: `linear-gradient(135deg, rgba(0, 0, 0, 0.65) 0%, ${alpha(theme.salon.secondary, 0.35)} 55%, ${alpha(theme.salon.primary, 0.55)} 100%)`,
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
          }}
        />

        {/* Text Content */}
        <Box sx={{ position: 'relative', width: '100%', color: 'white', px: 4 }}>
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              letterSpacing: 2,
              fontWeight: 700,
              opacity: 0.9,
              mb: 1.5,
              textAlign: 'center',
              color: theme.colors.primary[50]
            }}
          >
            {stepTitles[bookingStep] || t("client_services.your_booking")}
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
                  textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                  lineHeight: 1.1,
                  pl: { xs: 0, md: 2 }
                }}
              >
                {salonDetails?.name || t("client_services.your_salon")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <LocationOn sx={{ fontSize: 18, color: theme.colors.primary[300] }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    opacity: 0.9
                  }}
                >
                  {salonDetails?.address || salonDetails?.formatted_address || t("client_services.address_not_provided")}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  };

  const renderServiceImage = (service) => {
    const MEDIA_HEIGHT = 200;

    if (service.images && Array.isArray(service.images) && service.images.length > 0) {
      const currentIndex = currentImageIndices[service.service_id] || 0;
      const imageToUse = service.images[currentIndex];
      const imageUrl = imageToUse.image_url;

      if (imageUrl) {
        const fullImageUrl = buildImageUrl(imageUrl);

        return (
          <Box sx={{ position: 'relative', overflow: 'hidden' }}>
            <CardMedia
              component="img"
              height={MEDIA_HEIGHT}
              image={fullImageUrl}
              alt={service.name}
              sx={{
                objectFit: 'cover',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)'
                }
              }}
            />

            {service.images.length > 1 && (
              <>
                <IconButton
                  aria-label={t('client_services.previous_image')}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage(service.service_id);
                  }}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(8px)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                      transform: 'translateY(-50%) scale(1.1)',
                    },
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    width: 36,
                    height: 36
                  }}
                >
                  <ArrowBackIos sx={{ fontSize: 18, ml: 0.5 }} />
                </IconButton>

                <IconButton
                  aria-label={t('client_services.next_image')}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage(service.service_id);
                  }}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(8px)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                      transform: 'translateY(-50%) scale(1.1)',
                    },
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    width: 36,
                    height: 36
                  }}
                >
                  <ArrowForwardIos sx={{ fontSize: 18 }} />
                </IconButton>

                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 12,
                    right: 12,
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    backdropFilter: 'blur(8px)',
                    color: 'white',
                    borderRadius: 2,
                    padding: '4px 10px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }}
                >
                  {currentIndex + 1}/{service.images.length}
                </Box>
              </>
            )}
          </Box>
        );
      }
    }

    const hasImage = (service.image || service.image_url) &&
      (typeof service.image === 'string' || typeof service.image_url === 'string');

    const imageUrl = (() => {
      if (service.image && typeof service.image === 'string') {
        return service.image.startsWith('http') ? service.image : `${BASE_URL}${service.image}`;
      } else if (service.image_url && typeof service.image_url === 'string') {
        return service.image_url.startsWith('http') ? service.image_url : `${BASE_URL}${service.image_url}`;
      }
      return null;
    })();

    if (hasImage && imageUrl) {
      return (
        <CardMedia
          component="img"
          height={MEDIA_HEIGHT}
          image={imageUrl}
          alt={service.name}
          sx={{
            objectFit: 'cover',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)'
            }
          }}
        />
      );
    } else {
      return (
        <Box
          sx={{
            height: MEDIA_HEIGHT,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(135deg, ${theme.colors.primary[100]} 0%, ${theme.colors.primary[200]} 100%)`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            }
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              fontSize: '5rem',
              textTransform: 'uppercase',
              color: theme.colors.primary[600],
              textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              position: 'relative',
              zIndex: 1
            }}
          >
            {service.name ? service.name.charAt(0) : 'S'}
          </Typography>
        </Box>
      );
    }
  };

  return (
    <Container
      maxWidth="xl"
      style={{
        backgroundColor: theme.salon.background,
        fontFamily: theme.typography.fontFamily.sans.join(', '),
      }}
      sx={{
        py: 4,
        [HIGH_DENSITY_TABLET_MEDIA]: {
          maxWidth: 'min(96vw, 1760px)',
          py: 5,
        },
      }}
    >
      {renderUnifiedHeader()}
      {/* Étape 1: Mettre à jour pour passer à service_selection */}
      {bookingStep === 'type_selection' && (
        <AppointmentTypeSelector
          salonId={effectiveSalonId}
          onSelectSimple={() => {
            setBookingType('simple');
            setBookingStep('service_selection');
            // Si on avait déjà selectionné via "Choisir", on s'assure qu'il est dans la liste
            if (selectedService) {
              setSelectedServices([selectedService]);
            }
          }}
          onSelectConsecutive={() => {
            setBookingType('consecutive');
            setBookingStep('service_selection');
            // Si on avait déjà selectionné un service, il est le premier de la liste
            if (selectedService) {
              // Check if already in list to avoid duplicates (though state reset usually handles this)
              setSelectedServices([selectedService]);
            } else {
              setSelectedServices([]);
            }
          }}
          onBack={() => {
            if (startWithTypeSelection && onResetStartBooking) {
              // Started from ClientSalon "Prendre rendez-vous" button -> Go back to Overview tab
              onResetStartBooking();
            } else if (startBookingParam) {
              // Started via URL param (e.g. from Landing Page) -> Go back to Salon Details
              navigate(`/salon/${effectiveSalonId}`);
            } else {
              // Started from "Choisir" button in services list -> Go back to list
              setBookingStep('services');
              setSelectedService(null);
              setSelectedServices([]);
            }
          }}
          currentStep={1}
          totalSteps={5}
        />
      )}

      {/* Étape 2: Sélection des Prestations */}
      {bookingStep === 'service_selection' && (
        <ServiceSelectionStep
          salonId={effectiveSalonId}
          services={allServices || []}
          isConsecutive={bookingType === 'consecutive'}
          initialSelection={selectedServices}
          currentStep={2}
          totalSteps={5}
          onBack={() => setBookingStep('type_selection')}
          onCancel={() => {
            if (onResetStartBooking) onResetStartBooking();
            else navigate(`/salon/${effectiveSalonId}`);
          }}
          onContinue={(services) => {
            setSelectedServices(services);
            // On passe à l'étape choix du pro
            setBookingStep('pro_selection');
          }}
        />
      )}

      {/* Étape 3: Choix du Professionnel */}
      {bookingStep === 'pro_selection' && (
        <ProfessionalSelectionStep
          salonId={effectiveSalonId}
          selectedServices={selectedServices}
          employees={salonMembers || []}
          currentStep={3}
          totalSteps={5}
          onBack={() => setBookingStep('service_selection')}
          onCancel={() => {
            if (onResetStartBooking) onResetStartBooking();
            else navigate(`/salon/${effectiveSalonId}`);
          }}
          onContinue={(proMap) => {
            setSelectedProMap(proMap);

            // Mettre à jour selectedServices avec les teamMembers choisis
            const updatedServices = selectedServices.map(s => {
              const sId = s.service_id || s.id;
              return {
                ...s,
                teamMember: proMap[sId] // 'any' or ID
              };
            });
            setSelectedServices(updatedServices);

            setBookingStep('calendar');
          }}
        />
      )}

      {/* Étape 4: Date et Heure */}
      {bookingStep === 'calendar' && (
        <DateTimeSelectionStep
          salonId={effectiveSalonId}
          salonDetails={salonDetails}
          selectedServices={selectedServices}
          employees={salonMembers || []}
          currentStep={4}
          totalSteps={5}
          onBack={() => setBookingStep('pro_selection')}
          onCancel={() => {
            if (onResetStartBooking) onResetStartBooking();
            else navigate(`/salon/${effectiveSalonId}`);
          }}
          onContinue={(result) => {
            const { date, slot } = result;
            setBookingSlot({ date, slot });
            setBookingStep('confirmation');
          }}
        />
      )}

      {/* Étape Consecutive: Réservation de prestations consécutives */}
      {bookingStep === 'consecutive' && (
        <ConsecutiveServiceBooking
          salonId={effectiveSalonId}
          onClose={() => {
            setBookingStep('services');
            setSelectedService(null);
            setSelectedServices([]);
          }}
          onSuccess={(services) => {
            handleAppointmentSuccess(services);
            setBookingStep('services');
          }}
          salonDetails={salonDetails}
        />
      )}

      {/* Liste des services */}
      {bookingStep === 'services' && (
        <Fade in timeout={500}>
          <Box>
            {/* Header with background image - Toujours affiché */}
            <Box
              sx={{
                position: 'relative',
                minHeight: { xs: '50px', sm: '70px', md: '100px' },
                [HIGH_DENSITY_TABLET_MEDIA]: {
                  minHeight: 160,
                  mb: 5,
                },
                borderRadius: 3,
                overflow: 'hidden',
                mb: 4,
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
              }}
            >
              {/* Background Image */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundImage: salonDetails?.photos && salonDetails.photos.length > 0
                    ? `url(${buildImageUrl(typeof salonDetails.photos[0] === 'string' ? salonDetails.photos[0] : salonDetails.photos[0]?.image || salonDetails.photos[0]?.url || '')})`
                    : `linear-gradient(135deg, ${theme.colors.primary[400]} 0%, ${theme.colors.secondary[400]} 100%)`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'brightness(0.7)',
                  borderRadius: 3,
                }}
              />

              {/* Gradient Overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: `linear-gradient(135deg, rgba(0, 0, 0, 0.70) 0%, ${alpha(theme.salon.secondary, 0.30)} 55%, ${alpha(theme.salon.primary, 0.55)} 100%)`,
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  borderRadius: 3,
                }}
              />

              {/* Content */}
              <Box
                sx={{
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: { xs: 3, sm: 4, md: 5 },
                  [HIGH_DENSITY_TABLET_MEDIA]: {
                    p: 6,
                  },
                  color: 'white',
                }}
              >
                {/* Back and Home Buttons */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: { xs: 16, sm: 24 },
                    left: { xs: 16, sm: 24 },
                    right: { xs: 16, sm: 24 },
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'space-between'
                  }}
                >
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate(-1)}
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      px: { xs: 2, sm: 3 },
                      py: 1,
                      borderRadius: 2,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        transform: 'translateX(-4px)',
                        transition: 'all 0.2s'
                      }
                    }}
                  >
                    {t("client_services.back")}
                  </Button>
                  <Button
                    startIcon={<HomeIcon />}
                    onClick={() => navigate('/')}
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      px: { xs: 2, sm: 3 },
                      py: 1,
                      borderRadius: 2,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        transform: 'translateX(4px)',
                        transition: 'all 0.2s'
                      }
                    }}
                  >
                    {t("client_services.home")}
                  </Button>
                </Box>

                {/* Salon Name */}
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                    [HIGH_DENSITY_TABLET_MEDIA]: {
                      fontSize: 'clamp(3rem, 4vw, 4rem)',
                    },
                    mb: 1,
                    mt: { xs: 5, sm: 0 },
                    textAlign: 'center'
                  }}
                >
                  {salonDetails?.name || t("client_services.salon")}
                </Typography>

                {/* Address and Phone */}
                {(salonDetails?.address || salonDetails?.formatted_address || salonDetails?.phone) && (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                    {(salonDetails?.address || salonDetails?.formatted_address) && (
                      <Chip
                        icon={<LocationOn sx={{ color: 'white !important' }} />}
                        label={salonDetails?.address || salonDetails?.formatted_address}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          backdropFilter: 'blur(10px)',
                          color: 'white',
                          fontWeight: 500,
                          '& .MuiChip-icon': {
                            color: 'white'
                          }
                        }}
                      />
                    )}
                    {salonDetails.phone && (
                      <Chip
                        icon={<Phone sx={{ color: 'white !important' }} />}
                        label={salonDetails.phone}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          backdropFilter: 'blur(10px)',
                          color: 'white',
                          fontWeight: 500,
                          '& .MuiChip-icon': {
                            color: 'white'
                          }
                        }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Services Section - Afficher skeleton si en cours de chargement */}
            {servicesLoading ? (
              renderServicesSkeleton()
            ) : (
              <>
                {/* Nos Prestations Title */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 4,
                    background: theme.salon.surfaceMuted,
                    borderRadius: 3,
                    border: `1px solid ${theme.colors.border.light}`
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: theme.colors.text.primary,
                      mb: 3,
                      mt: { xs: 3, md: 0 },
                      textAlign: 'center',
                      fontSize: { xs: '1.5rem', md: '2rem' }
                    }}
                  >
                    {t("client_services.our_services")}
                  </Typography>

                  {/* Category Chips */}
                  <Box sx={{
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    mb: 3
                  }}>
                    <Chip
                      label={t("client_services.haircut_woman")}
                      sx={{
                        bgcolor: theme.colors.primary[100],
                        color: theme.colors.primary[700],
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        px: 2,
                        py: 1,
                        height: 'auto'
                      }}
                    />
                  </Box>

                  {/* Search Bar */}
                  <TextField
                    fullWidth
                    placeholder={t("client_services.search_service")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: 'white'
                      }
                    }}
                  />

                  {/* Category Filter Chips */}
                  <Box sx={{
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                    justifyContent: 'center'
                  }}>
                    {[t("client_services.all"), ...categories.map(cat => getTranslatedField(cat, 'name'))].map((category) => (
                      <Chip
                        key={category}
                        label={category}
                        clickable
                        onClick={() => setSelectedCategory(category)}
                        sx={{
                          bgcolor: selectedCategory === category ? theme.salon.primary : theme.colors.background.primary,
                          color: selectedCategory === category ? (theme.salon.compatibilityMode ? 'white' : theme.salon.onPrimary) : theme.colors.text.secondary,
                          fontWeight: 500,
                          '&:hover': {
                            bgcolor: selectedCategory === category ? theme.colors.primary[600] : theme.colors.primary[50]
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Paper>

                {/* Services List */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    px: { xs: 0, md: 6, lg: 8 },
                    [HIGH_DENSITY_TABLET_MEDIA]: {
                      px: 2,
                      gap: 5,
                    },
                  }}
                >
                  {Object.keys(servicesByCategory).map((categoryId, index) => {
                    const category = servicesByCategory[categoryId];
                    if (category.services.length === 0) return null;

                    // Filter services based on search term and category
                    const filteredServices = category.services.filter(service => {
                      const matchesSearch = searchTerm === '' ||
                        service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        (service.description && service.description.toLowerCase().includes(searchTerm.toLowerCase()));

                      const matchesCategory = selectedCategory === t("client_services.all") ||
                        category.categoryName.toLowerCase().includes(selectedCategory.toLowerCase()) ||
                        service.name.toLowerCase().includes(selectedCategory.toLowerCase());

                      return matchesSearch && matchesCategory;
                    });

                    if (filteredServices.length === 0) return null;

                    return (
                      <Box key={categoryId} sx={{ mb: 5 }}>
                        {/* Nom de la catégorie en orange */}
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 700,
                            color: theme.colors.secondary[700],
                            fontSize: "1.5rem",
                            [HIGH_DENSITY_TABLET_MEDIA]: {
                              fontSize: '1.8rem',
                            },
                            mb: 3,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            textAlign: 'left',
                          }}
                        >
                          {getTranslatedField(category, 'name') || category.categoryName}
                        </Typography>

                        {/* Tableau blanc des services */}
                        <Box
                          sx={{
                            backgroundColor: 'white',
                            borderRadius: 3,
                            overflow: 'hidden',
                            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                          }}
                        >
                          {filteredServices.map((service, serviceIndex) => {
                            const hasEmployee = salonMembersLoading ? true : serviceIdsWithEmployees.has(service.service_id);
                            const isClientBookingDisabled =
                              service?.client_booking_disabled === true ||
                              service?.client_booking_disabled === 'true';
                            const canClientBook = hasEmployee && !isClientBookingDisabled;

                            return (
                              <Box
                                key={service.service_id}
                                sx={{
                                  display: 'flex',
                                  flexDirection: { xs: 'column', md: 'row' },
                                  alignItems: { xs: 'stretch', md: 'flex-start' },
                                  gap: { xs: 2, md: 3 },
                                  p: 3,
                                  [HIGH_DENSITY_TABLET_MEDIA]: {
                                    p: 4,
                                    gap: 4,
                                  },
                                  borderBottom: serviceIndex < filteredServices.length - 1 ? `1px solid ${theme.colors.gray[200]}` : 'none',
                                  transition: 'background-color 0.2s ease',
                                  cursor: canClientBook ? 'pointer' : 'default',
                                  '&:hover': {
                                    backgroundColor: canClientBook ? theme.colors.gray[50] : 'inherit',
                                  },
                                }}
                                onClick={() => {
                                  if (!canClientBook) return;
                                  const newService = {
                                    serviceId: service.service_id,
                                    serviceName: service.name,
                                    selectedSlot: startTimeParam || null,
                                    selectedDate: startTimeParam ? new Date().toISOString().split('T')[0] : null,
                                    selectedTime: startTimeParam || null,
                                    teamMember: null
                                  };
                                  const updatedServices = [...selectedServices, newService];
                                  setSelectedServices(updatedServices);
                                  setSelectedService(service);
                                  setBookingStep('type_selection');
                                }}
                              >
                                {/* Nom + Description */}
                                <Box sx={{ flex: { xs: 'none', md: 1 }, minWidth: 0, width: { xs: '100%', md: 'auto' } }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: 600,
                                        color: theme.colors.gray[700],
                                        fontSize: { xs: '1.15rem', sm: '1.25rem' },
                                        [HIGH_DENSITY_TABLET_MEDIA]: {
                                          fontSize: '1.4rem',
                                        },
                                        mb: 0.5,
                                        textAlign: 'left',
                                      }}
                                    >
                                      {getTranslatedField(service, 'name')}
                                    </Typography>
                                    {service?.description && (
                                      <IconButton
                                        aria-label={t('client_services.service_details')}
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedServiceForDescription(service);
                                          setDescriptionDialogOpen(true);
                                        }}
                                        sx={{
                                          color: theme.colors.primary[600],
                                        }}
                                      >
                                        <Info sx={{ fontSize: 18 }} />
                                      </IconButton>
                                    )}
                                  </Box>
                                  {service?.description && (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: theme.colors.gray[500],
                                        fontSize: '0.875rem',
                                        [HIGH_DENSITY_TABLET_MEDIA]: {
                                          fontSize: '1rem',
                                        },
                                        lineHeight: 1.5,
                                        mb: { xs: 1, md: 0 },
                                      }}
                                    >
                                      {getTranslatedField(service, 'description')}
                                    </Typography>
                                  )}
                                </Box>

                                {/* Durée + Prix (sur mobile: même ligne) */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 2,
                                    width: { xs: '100%', md: 'auto' },
                                  }}
                                >
                                  {/* Durée */}
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    <AccessTime sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, color: theme.colors.gray[400] }} />
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: theme.colors.gray[600],
                                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {service.formatted_duration}
                                    </Typography>
                                  </Box>

                                  {/* Prix */}
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 700,
                                      color: theme.colors.gray[600],
                                      fontSize: '1.1rem',
                                      [HIGH_DENSITY_TABLET_MEDIA]: {
                                        fontSize: '1.25rem',
                                      },
                                      textAlign: 'right',
                                    }}
                                  >
                                    {service.price} DA
                                  </Typography>
                                </Box>

                                {/* Bouton Choisir ou message selon disponibilité des employés */}
                                {canClientBook ? (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: { xs: 'stretch', md: 'flex-end' },
                                      gap: 0.75,
                                      width: { xs: '100%', md: 'auto' },
                                      maxWidth: { xs: '100%', md: 240 },
                                    }}
                                  >
                                    <Button
                                    variant="contained"
                                    size="medium"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const newService = {
                                        serviceId: service.service_id,
                                        serviceName: getTranslatedField(service, 'name'),
                                        selectedSlot: startTimeParam || null,
                                        selectedDate: startTimeParam ? new Date().toISOString().split('T')[0] : null,
                                        selectedTime: startTimeParam || null,
                                        teamMember: null
                                      };
                                      const updatedServices = [...selectedServices, newService];
                                      setSelectedServices(updatedServices);
                                      setSelectedService(service);
                                      setBookingStep('type_selection');
                                    }}
                                    sx={{
                                      backgroundColor: theme.salon.primary,
                                      color: theme.salon.compatibilityMode ? 'white' : theme.salon.onPrimary,
                                      fontWeight: 600,
                                      fontSize: '0.875rem',
                                      [HIGH_DENSITY_TABLET_MEDIA]: {
                                        fontSize: '1rem',
                                        minWidth: 120,
                                      },
                                      textTransform: 'none',
                                      px: 3,
                                      py: 1,
                                      width: { xs: '100%', md: 'auto' },
                                      minWidth: { xs: 'auto', md: '100px' },
                                      '&:hover': {
                                        backgroundColor: theme.colors.primary[700],
                                      },
                                    }}
                                    >
                                      {t("client_services.choose")}
                                    </Button>
                                    <ManualBookingNotice bookingConfirmationMode={service?.booking_confirmation_mode} />
                                  </Box>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: theme.colors.warning[700],
                                      fontStyle: 'italic',
                                      fontSize: '0.85rem',
                                      textAlign: { xs: 'left', md: 'right' },
                                      maxWidth: { xs: '100%', md: 240 },
                                    }}
                                  >
                                    {t("client_services.contact_salon")}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </Box>
        </Fade>
      )}

      {bookingStep === 'confirmation' && (
        <Fade in timeout={500}>
          <Box>
            <AppointmentConfirmationStep
              salonDetails={salonDetails}
              selectedServices={selectedServices}
              selectedDate={bookingSlot?.date}
              selectedSlot={bookingSlot?.slot}
              onConfirm={handleConfirmBooking}
              onBack={() => setBookingStep('calendar')}
              onCancel={handleCancelBookingFlow}
              loading={isCreatingAppointment}
              employees={salonMembers}
            />
          </Box>
        </Fade>
      )}

      {/* Étape Calendar: Calendrier de réservation [LEGACY REMOVED] */}
      {bookingStep === 'calendar_DISABLED' && (
        <Fade in timeout={500}>
          <Box>
            {/* Header with background image */}
            <Box
              sx={{
                position: 'relative',
                minHeight: { xs: '150px', sm: '250px', md: '250px' },
                borderRadius: 3,
                overflow: 'visible',
                mb: 4,
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
              }}
            >
              {/* Background Image */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  minHeight: '100%',
                  backgroundImage: salonDetails?.photos && salonDetails.photos.length > 0
                    ? `url(${buildImageUrl(typeof salonDetails.photos[0] === 'string' ? salonDetails.photos[0] : salonDetails.photos[0]?.image || salonDetails.photos[0]?.url || '')})`
                    : `linear-gradient(135deg, ${theme.colors.primary[400]} 0%, ${theme.colors.secondary[400]} 100%)`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'brightness(0.7)',
                  borderRadius: 3,
                }}
              />

              {/* Gradient Overlay - Green & Brown like SalonDetails */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  minHeight: '100%',
                  background: `linear-gradient(135deg, rgba(0, 0, 0, 0.70) 0%, ${alpha(theme.salon.secondary, 0.30)} 55%, ${alpha(theme.salon.primary, 0.55)} 100%)`,
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  borderRadius: 3,
                }}
              />

              {/* Content */}
              <Box
                sx={{
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  p: { xs: 3, sm: 4, md: 5 },
                  color: 'white',
                }}
              >
                {/* Back and Home Buttons */}
                <Box
                  sx={{
                    display: { xs: 'flex', md: 'none' },
                    gap: 1,
                    justifyContent: 'space-between',
                    alignSelf: 'flex-start',
                    width: '100%',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={() => setShowAppointmentBooking(false)}
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      px: { xs: 2, sm: 3 },
                      py: 1,
                      borderRadius: 2,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        transform: 'translateX(-4px)',
                        transition: 'all 0.2s'
                      }
                    }}
                  >
                    {t("client_services.back")}
                  </Button>

                  <Button
                    startIcon={<HomeIcon />}
                    onClick={() => navigate('/')}
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      px: { xs: 2, sm: 3 },
                      py: 1,
                      borderRadius: 2,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        transform: 'translateX(4px)',
                        transition: 'all 0.2s'
                      }
                    }}
                  >
                    {t("client_services.home")}
                  </Button>
                </Box>
                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    position: 'absolute',
                    top: 24,
                    left: 24,
                    gap: 1,
                    justifyContent: 'space-between',
                    alignSelf: 'flex-start',
                    width: '100%',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={() => setShowAppointmentBooking(false)}
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      px: { xs: 2, sm: 3 },
                      py: 1,
                      borderRadius: 2,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      mx: { md: 2 },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        transform: 'translateX(-4px)',
                        transition: 'all 0.2s'
                      }
                    }}
                  >
                    {t("client_services.back")}
                  </Button>

                  <Button
                    startIcon={<HomeIcon />}
                    onClick={() => navigate('/')}
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      px: { xs: 2, sm: 3 },
                      py: 1,
                      borderRadius: 2,
                      marginRight: '50px',
                      fontSize: { xs: '0.65rem', sm: '0.875rem' },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        transform: 'translateX(4px)',
                        transition: 'all 0.2s'
                      }
                    }}
                  >
                    {t("client_services.home")}
                  </Button>
                </Box>

                {/* Title Section */}
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      mb: 2,
                      textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                      fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
                    }}
                  >
                    {salonDetails?.name || t("client_services.salon")}
                  </Typography>

                  {/* Service Info Card with Glassmorphism */}
                  <Box
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(20px)',
                      borderRadius: 3,
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      p: { xs: 2, md: 3 },
                      maxWidth: { xs: '100%', md: '600px' },
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                      mx: 'auto',
                      textAlign: 'center'
                    }}
                  >
                    {/* Label "Prestation sélectionnée" */}
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontWeight: 600,
                        letterSpacing: '1px',
                        fontSize: '0.75rem',
                        display: 'block',
                        mb: 1
                      }}
                    >
                      {t("client_services.selected_service")}
                    </Typography>

                    {/* Service Name */}
                    <Typography
                      variant="h4"
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        mb: 2,
                        fontSize: { xs: '1.5rem', sm: '1.75rem' }
                      }}
                    >
                      {selectedService?.serviceName || selectedService?.name || t("client_services.service")}
                    </Typography>

                    {/* Duration and Price Row */}
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {selectedService?.formatted_duration && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccessTime sx={{ color: 'white', fontSize: '1.2rem' }} />
                          <Typography
                            variant="body1"
                            sx={{
                              color: 'white',
                              fontWeight: 500,
                              fontSize: '1rem'
                            }}
                          >
                            {selectedService.formatted_duration}
                          </Typography>
                        </Box>
                      )}

                      {selectedService?.price && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CurrencyExchange sx={{ color: 'white', fontSize: '1.2rem' }} />
                          <Typography
                            variant="body1"
                            sx={{
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '1.1rem'
                            }}
                          >
                            {selectedService.price} DA
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            {showAppointmentBooking && (
              <AppointmentCalendar
                salonId={effectiveSalonId}
                selectedService={selectedService}
                existingServices={selectedServices}
                rebookAppointmentData={rebookAppointmentData}
                onSelectSlot={(slot) => {
                  if (slot.resetServices) {
                    setSelectedServices([]);
                    setSelectedService(null);
                  }
                  handleAppointmentBookingClose();
                }}
                onAddService={handleAppointmentSuccess}
                salonDetails={salonDetails}
                onClose={handleAppointmentBookingClose}
              />
            )}
          </Box>
        </Fade>
      )}

      {/* Service Description Dialog */}
      <AuthModal
        open={authModalOpen}
        onClose={() => {
          setAuthModalOpen(false);
          setIsPendingBooking(false); // Cancel pending booking if closed without login
        }}
        onAuthSuccess={() => {
          setAuthModalOpen(false);
          // Logic handled by useEffect watching 'user' state
        }}
      />

      <Dialog
        open={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
          navigate('/client/appointments');
        }}
      >
        <DialogTitle sx={{ bgcolor: theme.colors.success[100], color: theme.colors.success[800] }}>{t(`appointment_workflow.booking.${bookingOutcome}_title`, { defaultValue: t(`appointment_workflow.booking.${bookingOutcome}`) })}</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography>{t(`appointment_workflow.booking.${bookingOutcome}`)}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowSuccessDialog(false);
            navigate('/client/appointments');
          }} variant="contained" color="success">
            {t("client_services.view_my_appointments")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={descriptionDialogOpen}
        onClose={() => setDescriptionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            color: theme.colors.text.primary,
            fontSize: "1.25rem",
            borderBottom: `1px solid ${theme.colors.border.light}`,
            pb: 2
          }}
        >
          {selectedServiceForDescription?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography
            variant="body1"
            sx={{
              color: theme.colors.text.tertiary,
              lineHeight: 1.6,
              fontSize: "1rem"
            }}
          >
            {selectedServiceForDescription?.description}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setDescriptionDialogOpen(false)}
            sx={{
              color: theme.colors.primary[500],
              fontWeight: 600,
              textTransform: "none",
              '&:hover': {
                backgroundColor: alpha(theme.salon.primary, 0.1),
              }
            }}
          >
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClientServices;
